/**
 * ChunkingRenderer - Renderer for chunked transfer settings
 *
 * Allows configuring chunked upload/download for large data transfers.
 */

import { makeStyles, tokens, Text, Switch, SpinButton, Field } from '@coreai/fluentui-react';
import type { SettingRendererProps } from './registry';
import { registerSettingRenderer } from './registry';
import type { UploadChunk } from '@/types/nodeSettings';

import {
  settingContainerStyle,
  settingDescriptionStyle,
  settingHeaderStyle,
  settingLabelContainerStyle,
  settingLabelStyle,
} from './settingRendererStyles';

const useStyles = makeStyles({
  container: settingContainerStyle,
  header: settingHeaderStyle,
  labelContainer: settingLabelContainerStyle,
  label: settingLabelStyle,
  description: settingDescriptionStyle,
  inputContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  spinButton: {
    width: '120px',
  },
  unit: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
});

export interface ChunkingRendererProps extends SettingRendererProps<UploadChunk> {
  label?: string;
  description?: string;
  /** Whether this is for download (affects default values) */
  isDownload?: boolean;
}

/**
 * ChunkingRenderer component for chunked transfer configuration
 */
export const ChunkingRenderer = ({
  setting,
  onChange,
  readOnly = false,
  label = 'Chunked Transfer',
  description = 'Transfer large files in chunks',
  isDownload = false,
}: ChunkingRendererProps) => {
  const styles = useStyles();

  if (!setting.isSupported) {
    return null;
  }

  const currentValue = setting.value ?? {};
  const isEnabled = currentValue.transferMode === 'chunked';

  const handleToggle = (_: React.ChangeEvent<HTMLInputElement>, data: { checked: boolean }) => {
    if (data.checked) {
      onChange({
        transferMode: 'chunked',
        uploadChunkSize: currentValue.uploadChunkSize ?? 10,
      });
    } else {
      onChange(undefined);
    }
  };

  const handleSizeChange = (_: React.SyntheticEvent, data: { value?: number | null }) => {
    if (data.value !== undefined && data.value !== null) {
      onChange({
        ...currentValue,
        transferMode: 'chunked',
        uploadChunkSize: data.value,
      });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.labelContainer}>
          <Text className={styles.label}>{label}</Text>
          <Text className={styles.description}>{description}</Text>
        </div>
        <Switch
          checked={isEnabled}
          onChange={handleToggle}
          disabled={readOnly}
          aria-label={`Enable ${label}`}
        />
      </div>

      {isEnabled && (
        <div className={styles.inputContainer}>
          <Field label={isDownload ? 'Download chunk size' : 'Upload chunk size'}>
            <SpinButton
              className={styles.spinButton}
              value={currentValue.uploadChunkSize ?? 10}
              onChange={handleSizeChange}
              min={1}
              max={100}
              step={1}
              disabled={readOnly}
              aria-label="Chunk size in MB"
            />
          </Field>
          <Text className={styles.unit}>MB</Text>
        </div>
      )}
    </div>
  );
};

ChunkingRenderer.displayName = 'ChunkingRenderer';

registerSettingRenderer('chunking', ChunkingRenderer as React.ComponentType<SettingRendererProps<unknown>>);
